import type { GridPoint, GridTile } from "../types/grid";
import type { LevelData, LevelGridData } from "../types/level";
import type { LevelFogState, LevelState } from "../model/LevelState";
import { getFogAlpha, getPortalColor, RENDER_PALETTE, TILE_SIZE } from "./render-palette";

function computeTileFogAlpha(point: GridPoint, fogState: LevelFogState): number {
  if (fogState.debugReveal) {
    return getFogAlpha("debugReveal");
  }
  if (fogState.permanentReveal) {
    return getFogAlpha("permanentReveal");
  }
  if (isPointInList(point, fogState.visible)) {
    return getFogAlpha("visible");
  }
  if (isPointInList(point, fogState.explored)) {
    return getFogAlpha("explored");
  }
  return getFogAlpha("hidden");
}

export interface TerrainDrawInstruction {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly color: number;
  readonly type: GridTile;
}

export interface PlankDrawInstruction {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly color: number;
}

export interface EntityDrawInstruction {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly color: number;
  readonly kind: string;
  readonly zIndex: number;
  readonly fogAlpha: number;
}

export interface EntityVisibilityOverride {
  readonly forceVisibleCollectibleIds?: readonly string[];
  readonly forceVisibleBreadcrumbIds?: readonly string[];
}

export interface FogMaskInstruction {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly alpha: number; // 0 = hidden (don't draw), 1.0 = visible, 0.5 = half-visible debug
}

export type FogMaskDebugMode = 'full' | 'half';

export interface FogDrawInstruction {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly alpha: number;
}

export interface ViewportBounds {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

const TILE_TYPES: readonly GridTile[] = ["empty", "solid", "ladder"];
const PLANK_HEIGHT = 4;
const PLAYER_SIZE = 18;
const PLAYER_OFFSET = 3;
export const COLLECTIBLE_SIZE = 16;
export const COLLECTIBLE_OFFSET = 4;
const PORTAL_SIZE = 20;
const PORTAL_OFFSET = 2;
export const BREADCRUMB_SIZE = 12;
export const BREADCRUMB_OFFSET = 6;

function isPointInList(point: GridPoint, list: readonly GridPoint[]): boolean {
  return list.some((p) => p.x === point.x && p.y === point.y);
}

export function getTerrainDrawInstructions(grid: LevelGridData): TerrainDrawInstruction[] {
  const instructions: TerrainDrawInstruction[] = [];
  for (let i = 0; i < grid.tiles.length; i += 1) {
    const tileValue = grid.tiles[i];
    if (tileValue === undefined) {
      continue;
    }
    const tileType = TILE_TYPES[tileValue];
    if (tileType === undefined) {
      continue;
    }
    const x = (i % grid.width) * TILE_SIZE;
    const y = Math.floor(i / grid.width) * TILE_SIZE;
    let color: number;
    if (tileType === "solid") {
      color = RENDER_PALETTE.terrain.solid;
    } else if (tileType === "ladder") {
      color = RENDER_PALETTE.terrain.ladder;
    } else {
      color = RENDER_PALETTE.terrain.empty;
    }
    instructions.push({
      x,
      y,
      width: TILE_SIZE,
      height: TILE_SIZE,
      color,
      type: tileType,
    });
  }
  return instructions;
}

export function getPlankDrawInstructions(grid: LevelGridData): PlankDrawInstruction[] {
  return grid.planks.map((plank) => ({
    x: plank.x * TILE_SIZE,
    y: plank.y * TILE_SIZE + (TILE_SIZE - PLANK_HEIGHT),
    width: TILE_SIZE,
    height: PLANK_HEIGHT,
    color: RENDER_PALETTE.terrain.plank,
  }));
}

export function getEntityDrawInstructions(
  data: LevelData,
  state: LevelState,
  playerOverride?: GridPoint,
  flashPlayer?: boolean,
  hideNonPlayerEntities?: boolean,
  visibilityOverride?: EntityVisibilityOverride,
): EntityDrawInstruction[] {
  const instructions: EntityDrawInstruction[] = [];
  const collectedIds = new Set(state.collectibles.local);
  const forceVisibleCollectibleIds = new Set(
    visibilityOverride?.forceVisibleCollectibleIds ?? [],
  );
  const forceVisibleBreadcrumbIds = new Set(
    visibilityOverride?.forceVisibleBreadcrumbIds ?? [],
  );

  const playerPos: GridPoint = playerOverride ?? state.player;
  const playerFogAlpha = computeTileFogAlpha(playerPos, state.fog);

  instructions.push({
    x: playerPos.x * TILE_SIZE + PLAYER_OFFSET,
    y: playerPos.y * TILE_SIZE + PLAYER_OFFSET,
    width: PLAYER_SIZE,
    height: PLAYER_SIZE,
    color: flashPlayer === true
      ? RENDER_PALETTE.entities.playerFlash
      : RENDER_PALETTE.entities.player,
    kind: "player",
    zIndex: 3,
    fogAlpha: playerFogAlpha,
  });

  const exitFogAlpha = computeTileFogAlpha(data.exit, state.fog);

  if (!hideNonPlayerEntities) {
    instructions.push({
      x: data.exit.x * TILE_SIZE,
      y: data.exit.y * TILE_SIZE,
      width: TILE_SIZE,
      height: TILE_SIZE,
      color: RENDER_PALETTE.entities.exit,
      kind: "exit",
      zIndex: 3,
      fogAlpha: exitFogAlpha,
    });

    for (const collectible of data.collectibles) {
      if (
        collectedIds.has(collectible.id) &&
        !forceVisibleCollectibleIds.has(collectible.id)
      ) {
        continue;
      }
      const collectibleFogAlpha = computeTileFogAlpha(collectible, state.fog);
      instructions.push({
        x: collectible.x * TILE_SIZE + COLLECTIBLE_OFFSET,
        y: collectible.y * TILE_SIZE + COLLECTIBLE_OFFSET,
        width: COLLECTIBLE_SIZE,
        height: COLLECTIBLE_SIZE,
        color: RENDER_PALETTE.entities.collectible,
        kind: "collectible",
        zIndex: 1,
        fogAlpha: collectibleFogAlpha,
      });
    }

    const triggeredIds = new Set(state.breadcrumbs.local);

    for (const breadcrumb of state.breadcrumbs.active) {
      if (
        triggeredIds.has(breadcrumb.id) &&
        !forceVisibleBreadcrumbIds.has(breadcrumb.id)
      ) continue;
      if (!breadcrumb.showIcon) continue;

      const breadcrumbFogAlpha = computeTileFogAlpha(breadcrumb, state.fog);
      instructions.push({
        x: breadcrumb.x * TILE_SIZE + BREADCRUMB_OFFSET,
        y: breadcrumb.y * TILE_SIZE + BREADCRUMB_OFFSET,
        width: BREADCRUMB_SIZE,
        height: BREADCRUMB_SIZE,
        color: RENDER_PALETTE.entities.breadcrumb,
        kind: "breadcrumb",
        zIndex: 1,
        fogAlpha: breadcrumbFogAlpha,
      });
    }

    for (const portal of data.portals) {
      instructions.push({
        x: portal.x * TILE_SIZE + PORTAL_OFFSET,
        y: portal.y * TILE_SIZE + PORTAL_OFFSET,
        width: PORTAL_SIZE,
        height: PORTAL_SIZE,
        color: getPortalColor(portal.group),
        kind: "portal",
        zIndex: 1,
        fogAlpha: 0,
      });
    }
  }

  return instructions;
}

export function getFogMaskDrawInstructions(
  grid: LevelGridData,
  fogState: LevelFogState,
  debugMode: FogMaskDebugMode = "full",
): FogMaskInstruction[] {
  const instructions: FogMaskInstruction[] = [];
  for (let y = 0; y < grid.height; y += 1) {
    for (let x = 0; x < grid.width; x += 1) {
      const point = { x, y };
      let alpha: number;

      if (fogState.debugReveal || fogState.permanentReveal) {
        alpha = 1.0;
      } else if (isPointInList(point, fogState.visible)) {
        alpha = 1.0;
      } else if (isPointInList(point, fogState.explored)) {
        alpha = debugMode === "half" ? 0.5 : 0;
      } else {
        alpha = debugMode === "half" ? 0.5 : 0;
      }

      if (alpha > 0) {
        instructions.push({
          x: x * TILE_SIZE,
          y: y * TILE_SIZE,
          width: TILE_SIZE,
          height: TILE_SIZE,
          alpha,
        });
      }
    }
  }
  return instructions;
}

/** @deprecated Kept for reference and test compatibility. Use getFogMaskDrawInstructions for BitmapMask rendering. */
export function getFogDrawInstructions(
  grid: LevelGridData,
  fogState: LevelFogState,
  _alwaysVisible?: readonly GridPoint[],
): FogDrawInstruction[] {
  const instructions: FogDrawInstruction[] = [];
  for (let y = 0; y < grid.height; y += 1) {
    for (let x = 0; x < grid.width; x += 1) {
      const alpha = computeTileFogAlpha({ x, y }, fogState);
      instructions.push({
        x: x * TILE_SIZE,
        y: y * TILE_SIZE,
        width: TILE_SIZE,
        height: TILE_SIZE,
        alpha,
      });
    }
  }
  return instructions;
}

export function getWorldBounds(grid: LevelGridData): { width: number; height: number } {
  return {
    width: grid.width * TILE_SIZE,
    height: grid.height * TILE_SIZE,
  };
}

export function getOutOfLevelFogOverlayInstructions(
  viewport: ViewportBounds,
  world: { readonly width: number; readonly height: number },
): FogDrawInstruction[] {
  const instructions: FogDrawInstruction[] = [];

  if (viewport.left < 0) {
    const width = Math.min(0, viewport.right) - viewport.left;
    if (width > 0) {
      instructions.push({
        x: viewport.left,
        y: viewport.top,
        width,
        height: viewport.bottom - viewport.top,
        alpha: 1,
      });
    }
  }

  if (viewport.right > world.width) {
    const width = viewport.right - Math.max(world.width, viewport.left);
    if (width > 0) {
      instructions.push({
        x: Math.max(world.width, viewport.left),
        y: viewport.top,
        width,
        height: viewport.bottom - viewport.top,
        alpha: 1,
      });
    }
  }

  if (viewport.top < 0) {
    const left = Math.max(viewport.left, 0);
    const right = Math.min(viewport.right, world.width);
    const width = right - left;
    if (width > 0) {
      instructions.push({
        x: left,
        y: viewport.top,
        width,
        height: Math.min(0, viewport.bottom) - viewport.top,
        alpha: 1,
      });
    }
  }

  if (viewport.bottom > world.height) {
    const left = Math.max(viewport.left, 0);
    const right = Math.min(viewport.right, world.width);
    const width = right - left;
    if (width > 0) {
      instructions.push({
        x: left,
        y: Math.max(world.height, viewport.top),
        width,
        height: viewport.bottom - Math.max(world.height, viewport.top),
        alpha: 1,
      });
    }
  }

  return instructions;
}
